import { v4 as uuidv4 } from "uuid";
import { singleOrderDetailsGenericUpdateForPostgres } from "../../../api/postgreSQL/postCalls/singleOrderDetailsGenericUpdateForPostgres";
import { SingleOrderDetailsTableName } from "../../../enums/singleOrderDetailsTableName";
import { logError } from "../../../utils/customLogger";
import { uploadToFirebaseStorage } from "../../../utils/fileUpload";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { generatePathForFileUpload } from "../../finalDocuments/utils/generatePathForFileUpload";

interface UploadClinicalAttachmentParams {
  mergedFile: Blob;
  orderId: string;
  onSuccess?: (storagePath: string) => void;
  onFailure?: (error: Error) => void;
  onLoadingChange?: (isLoading: boolean) => void;
}

/**
 * Uploads a clinical attachment to Firebase Storage and persists the resulting
 * `file_path` to Postgres via the generic single-order-details update endpoint
 * (`clinical-document` table).
 *
 * Generates a unique file ID (UUID) for each upload to avoid collisions.
 *
 * @param params - Upload configuration
 * @param params.mergedFile - The PDF blob to upload.
 * @param params.orderId - The medical PA order ID used to build the storage
 *   path and identify the Firestore document.
 * @param params.onSuccess - Called with the resulting storage path after a
 *   successful upload.
 * @param params.onFailure - Called with the error if the upload fails.
 * @param params.onLoadingChange - Called with `true` when the upload starts
 *   and `false` when it completes (regardless of success or failure).
 */
export const uploadClinicalAttachmentToFirebase = async ({
  mergedFile,
  orderId,
  onSuccess,
  onFailure,
  onLoadingChange,
}: UploadClinicalAttachmentParams): Promise<void> => {
  const fileId = uuidv4();
  const filePath = generatePathForFileUpload(orderId, fileId);

  onLoadingChange?.(true);
  try {
    const storagePath = await uploadToFirebaseStorage({
      file: mergedFile,
      storagePath: filePath,
      fileName: `${fileId}.pdf`,
    });

    await singleOrderDetailsGenericUpdateForPostgres<{ file_path: string }>({
      order_id: orderId,
      org_id: getOrgIdForFetchExternalWorklist(),
      table_name: SingleOrderDetailsTableName.CLINICAL_DOCUMENT,
      data: { file_path: storagePath },
    });

    onSuccess?.(storagePath);
  } catch (error) {
    logError(error as Error, "Failed to upload clinical attachment");
    onFailure?.(error as Error);
  } finally {
    onLoadingChange?.(false);
  }
};
